<template>
  <div :class="b(modifiers)">
    <div :class="b('inner', innerModifiers)">
      <div
        v-if="status.code !== ELECTRONIC_INSTRUCTION_STATUS_MAP.VALID"
        :class="b('status-indicator')"
      >
        <e-icon-sprite
          icon="i-exclamation"
          size="20"
        />
      </div>
      <div
        v-if="['default', 'archive'].includes(content)"
        :class="b('icon')"
      >
        <e-icon-sprite
          icon="i-electronic-instruction"
          size="60"
        />
      </div>
      <div :class="b('information')">
        <h3 :class="b('title')">
          {{ getElectronicInstructionBrandNames(item) }}
        </h3>
        <dl :class="b('properties')">
          <div :class="b('property')">
            <dt :class="b('property-name')">
              {{ $t('c-electronic-instruction-tile.category') }}
            </dt>
            <dd :class="b('property-value')">
              {{ item.category }}
            </dd>
          </div>

          <template v-if="['default', 'archive'].includes(content) && searchReference">
            <div :class="b('property')">
              <dt :class="b('property-name')">
                {{ searchReference.label }}
              </dt>
              <dd :class="b('property-value')">
                {{ searchReference.value }}
                <button
                  :class="b('more-toggle')"
                  type="button"
                  @click="onDetailClick(true)"
                >
                  {{ $t('c-electronic-instruction-tile.moreToggleLabel') }}
                </button>
              </dd>
            </div>
          </template>
          <template v-else>
            <template v-if="item.referenceNumbers && item.referenceNumbers.length">
              <div
                :ref="PROPERTY_REF_MAP.REFERENCE_NUMBER"
                :class="b('property', getModifiersForLimitedProperty(PROPERTY_REF_MAP.REFERENCE_NUMBER))"
              >
                <dt :class="b('property-name')">
                  {{ $t('c-electronic-instruction-tile.referenceNumber') }}
                </dt>
                <dd :class="b('property-value', { refNumbers: true })">
                  {{ item.referenceNumbers.join(', ') }}
                </dd>
              </div>
              <div :class="b('property-toggle-wrapper')">
                <button
                  :class="b('property-toggle')"
                  type="button"
                  @click="onPropertyCollapseToggle(PROPERTY_REF_MAP.REFERENCE_NUMBER)"
                >
                  <template v-if="expandedProperties[PROPERTY_REF_MAP.REFERENCE_NUMBER]">
                    {{ $t('c-electronic-instruction-tile.propertyToggleLabelCollapse') }}
                  </template>
                  <template v-else>
                    {{ $t('c-electronic-instruction-tile.propertyToggleLabelExpand') }}
                  </template>
                </button>
              </div>
            </template>

            <template v-if="item.udiDiNumbers && item.udiDiNumbers.length">
              <div
                :ref="PROPERTY_REF_MAP.UDI_DI_NUMBER"
                :class="b('property', getModifiersForLimitedProperty(PROPERTY_REF_MAP.UDI_DI_NUMBER))"
              >
                <dt :class="b('property-name')">
                  {{ $t('c-electronic-instruction-tile.udiDiNumber') }}
                </dt>
                <dd :class="b('property-value')">
                  {{ item.udiDiNumbers.join(', ') }}
                </dd>
              </div>
              <div :class="b('property-toggle-wrapper')">
                <button
                  :class="b('property-toggle')"
                  type="button"
                  @click="onPropertyCollapseToggle(PROPERTY_REF_MAP.UDI_DI_NUMBER)"
                >
                  <template v-if="expandedProperties[PROPERTY_REF_MAP.UDI_DI_NUMBER]">
                    {{ $t('c-electronic-instruction-tile.propertyToggleLabelCollapse') }}
                  </template>
                  <template v-else>
                    {{ $t('c-electronic-instruction-tile.propertyToggleLabelExpand') }}
                  </template>
                </button>
              </div>
            </template>
          </template>

          <template v-if="['preview'].includes(content) && item.languages && item.languages.length">
            <div
              :ref="PROPERTY_REF_MAP.LANGUAGES"
              :class="b('property', getModifiersForLimitedProperty(PROPERTY_REF_MAP.LANGUAGES))"
            >
              <dt :class="b('property-name')">
                {{ $t('c-electronic-instruction-tile.languages') }}
              </dt>
              <dd :class="b('property-value')">
                {{ item.languages.join(', ') }}
              </dd>
            </div>
            <div :class="b('property-toggle-wrapper')">
              <button
                :class="b('property-toggle')"
                type="button"
                @click="onPropertyCollapseToggle(PROPERTY_REF_MAP.LANGUAGES)"
              >
                <template v-if="expandedProperties[PROPERTY_REF_MAP.LANGUAGES]">
                  {{ $t('c-electronic-instruction-tile.propertyToggleLabelCollapse') }}
                </template>
                <template v-else>
                  {{ $t('c-electronic-instruction-tile.propertyToggleLabelExpand') }}
                </template>
              </button>
            </div>
          </template>

          <template v-if="['preview'].includes(content)">
            <template v-if="item.countriesApplicable && item.countriesApplicable.length">
              <div :class="b('property')">
                <dt :class="b('property-name')">
                  {{ $t('c-electronic-instruction-tile.countriesApplicable') }}
                </dt>
                <dd :class="b('property-value')">
                  {{ item.countriesApplicable.join(', ') }}
                </dd>
              </div>
            </template>

            <template v-if="item.fileSize">
              <div :class="b('property')">
                <dt :class="b('property-name')">
                  {{ $t('c-electronic-instruction-tile.fileSize') }}
                </dt>
                <dd :class="b('property-value')">
                  {{ item.fileSize }}
                </dd>
              </div>
            </template>

            <div :class="b('property')">
              <dt :class="b('property-name')">
                {{ $t('c-electronic-instruction-tile.fileId') }}
              </dt>
              <dd :class="b('property-value')">
                {{ item.fileId }}
              </dd>
            </div>

            <div :class="b('property')">
              <dt :class="b('property-name')">
                {{ $t('c-electronic-instruction-tile.fileType') }}
              </dt>
              <dd :class="b('property-value', { fileType: true })">
                {{ $t('c-electronic-instruction-tile.fileTypePDF') }}
              </dd>
            </div>
          </template>
        </dl>

        <template v-if="['default', 'archive'].includes(content) && item.languages?.length">
          <div
            v-if="item.languages.length > 1"
            v-tooltip:top-start="item.languages.join(', ')"
            :class="b('available-languages')"
          >
            {{ $t('c-electronic-instruction-tile.availableLanguages') }}
          </div>
          <div
            v-else-if="['archive'].includes(content)"
            :class="b('single-language')"
          >
            {{ item.languages[0] }}
          </div>
        </template>
        <div :class="b('status-text')">
          {{ status.text || '' }}
          <div
            v-if="status.validAsOf"
            :class="b('valid-as-of')"
          >
            {{ status.validAsOf }}
          </div>
        </div>
      </div>
      <div :class="b('actions')">
        <e-button
          v-if="['default'].includes(actions) || (['history'].includes(actions) && item.validAsOf)"
          :class="b('cta')"
          :width="$viewport.isSm && width === 'responsive' ? 'auto' : 'full'"
          spacing="600"
          :variant="matchTheme(themeConfig.buttons.variant)"
          @click="onDetailClick"
        >
          {{ $t('c-electronic-instruction-tile.view') }}
        </e-button>
        <template v-if="['preview', 'preview-history'].includes(actions)">
          <e-button
            v-if="item.fileUrl"
            :class="b('cta')"
            :width="$viewport.isSm && width === 'responsive' ? 'auto' : 'full'"
            :variant="actions === 'preview' ? 'primary' : matchTheme(themeConfig.buttons.variant)"
            spacing="600"
            @click="onDownloadClick"
          >
            {{ $t('c-electronic-instruction-tile.download') }}
          </e-button>
        </template>
        <e-button
          v-if="['preview'].includes(actions)"
          :class="b('cta')"
          :width="$viewport.isSm && width === 'responsive' ? 'auto' : 'full'"
          :variant="matchTheme(themeConfig.buttons.variant)"
          spacing="600"
          @click="onReportClick"
        >
          {{ $t('c-electronic-instruction-tile.reportProblem') }}
        </e-button>
        <div
          v-if="['preview'].includes(actions)"
          :class="b('request-disclaimer')"
        >
          <p>{{ $t('c-electronic-instruction-tile.requestDisclaimer') }}</p>
          <button
            :class="b('request-cta')"
            type="button"
            @click="onRequestHardCopyClick"
          >
            {{ $t('c-electronic-instruction-tile.requestHardCopy') }}
          </button>
        </div>
      </div>
    </div>
    <ul
      v-if="history"
      :class="b('history')"
    >
      <template v-if="isHistoryExpanded">
        <li
          v-for="historyItem in eifuStore.history[item.id]"
          :key="historyItem.fileId"
          :class="b('history-item')"
        >
          <c-electronic-instruction-tile
            ref="historyItem"
            :item="historyItem"
            :query="query"
            actions="history"
            :show-subscription-on-download="false"
          />
        </li>

        <li :class="b('archive')">
          <c-slider-1
            :class="b('archived-items')"
            :variant="$viewport.isSm ? 'auto-width' : 'fullwidth'"
            padding="0"
          >
            <c-electronic-instruction-tile
              v-for="archivedItem in eifuStore.relations[item.id]"
              :key="archivedItem.fileId"
              ref="archivedItem"
              :item="archivedItem"
              :related-asset-parent-item="item"
              :query="query"
              width="narrow"
              vertical
              content="archive"
              :show-subscription-on-download="false"
            />
          </c-slider-1>
        </li>
      </template>

      <li :class="b('history-toggle-wrapper')">
        <e-button
          :progress="isLoadingHistory"
          :disabled="disableHistoryToggle"
          :variant="matchTheme(themeConfig.buttons.variant)"
          spacing="600"
          width="auto"
          @click="onHistoryToggleClick"
        >
          {{ historyToggleLabel }}
        </e-button>
      </li>
    </ul>
  </div>
</template>

<script>
  import { ELECTRONIC_INSTRUCTION_STATUS_MAP, SUPPORTS_INTERSECTION_OBSERVER, URL_PARAM_MAP } from '@/setup/globals';
  import useEifuStore from '@/stores/eifu';
  import useNotificationStore from '@/stores/notification';
  import cSlider1 from '@/components/c-slider-1.vue';
  import eButton from '@/elements/e-button.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import downloadFileByUrl from '@/helpers/download-file.js';
  import { formatDate } from '@/helpers/format-date-time.js';
  import getElectronicInstructionBrandNames from '@/helpers/get-electronic-instruction-brand-names.js';
  import themes from '@/mixins/themes.js';
  import tooltip from '@/plugins/tooltip/directive.js';

  /**
   * A list of properties with limited height applied (accordion).
   *
   * @type {Object}
   */
  const PROPERTY_REF_MAP = {
    REFERENCE_NUMBER: 'property--reference-number',
    UDI_DI_NUMBER: 'property--udi-di-number',
    LANGUAGES: 'property--languages',
  };

  const findIgnoreCase = (array, value) => {
    for (let i = 0; i < array?.length; i += 1) {
      if (`${array[i]}`.toLowerCase() === `${value}`.toLowerCase()) {
        return array[i];
      }
    }

    return null;
  };

  /**
   * Renders an electronic instruction tile.
   */
  export default {
    name: 'c-electronic-instruction-tile',
    components: {
      cSlider1,
      eButton,
      eIconSprite,
    },

    directives: {
      tooltip,
    },
    mixins: [themes],

    props: {
      /**
       * Expects an electronic instruction object.
       */
      item: {
        type: Object,
        required: true,
      },

      /**
       * Expects an electronic instruction object.
       */
      relatedAssetParentItem: {
        type: Object,
        default: null,
        required: false,
      },

      /**
       * Allows to define the border style.
       */
      border: {
        type: String,
        default: 'default',
        validator(value) {
          return ['default', 'gradient', 'none'].includes(value);
        },
      },

      /**
       * Allows to render a specific width of the component.
       */
      width: {
        type: String,
        default: 'responsive',
        validator(value) {
          return ['responsive', 'narrow'].includes(value);
        },
      },

      /**
       * Allows specifying which meta information should be shown on the component.
       */
      content: {
        type: String,
        default: 'default',
        validator(value) {
          return [
            'default',
            'preview', // Detail modal
            'archive', // Archived items slider
          ].includes(value);
        },
      },

      /**
       * Allows specifying which actions should be shown on the component.
       */
      actions: {
        type: String,
        default: 'default',
        validator(value) {
          return [
            'default', // Search results
            'history', // Search results - history
            'preview', // Detail modal
            'preview-history', // Detail modal - history
            'none', // No actions at all
          ].includes(value);
        },
      },

      /**
       * Allows to define a query, which will be used to reduce refNumber and/or UDI-DI details.
       */
      query: {
        type: String,
        default: null,
      },

      /**
       * Enables child list with history items.
       */
      history: {
        type: Boolean,
        default: false,
      },

      /**
       * Shows subscription overlay when a download is started.
       */
      showSubscriptionOnDownload: {
        type: Boolean,
        default: true,
      },

      /**
       * Enable vertical layout.
       */
      vertical: {
        type: Boolean,
        default: false,
      },
    },
    setup() {
      return {
        eifuStore: useEifuStore(),
        notificationStore: useNotificationStore(),
      };
    },
    data() {
      return {
        ELECTRONIC_INSTRUCTION_STATUS_MAP,
        PROPERTY_REF_MAP,
        SUPPORTS_INTERSECTION_OBSERVER,

        /**
         * @type {Array.<String>} Holds a list of ref identifiers, that relate to properties with text overflow.
         */
        overflowingProperties: [],

        /**
         * @type {Object} Holds flags for the open/close state of properties with limited height.
         */
        expandedProperties: {},

        /**
         * @type {Boolean} Defines it the history items should be visible.
         */
        isHistoryExpanded: false,

        /**
         * @type {Boolean} Determines if the history is currently laoding for this instance.
         */
        isLoadingHistory: false,

        /**
         * @type {IntersectionObserver} Holds the component related IntersectionObserver instance.
         */
        intersectionObserver: null,

        /**
         * @type {Object} Holds theme specific options for the component.
         */
        themeConfig: {
          buttons: {
            variant: {
              default: 'default',
              sagemax: 'secondary',
            },
          },
        },
      };
    },

    computed: {
      /**
       * Returns the label for the history toggle button based on the current display mode.
       *
       * @returns {String}
       */
      historyToggleLabel() {
        const key =
          !this.disableHistoryToggle && this.isHistoryExpanded
            ? 'c-electronic-instruction-tile.showLess'
            : 'c-electronic-instruction-tile.earlierVersions';

        return this.$t(key);
      },

      /**
       * Searches for the search term in udiDiNumbers and referenceNumbers to be able to show condensed search results.
       *
       * @returns {null|Object}
       */
      searchReference() {
        const { item, query } = this;

        let match = findIgnoreCase(item.udiDiNumbers, query);

        if (match) {
          return {
            label: this.$t('c-electronic-instruction-tile.udiDiNumber'),
            value: match,
          };
        }

        match = findIgnoreCase(item.referenceNumbers, query);

        if (match) {
          return {
            label: this.$t('c-electronic-instruction-tile.referenceNumber'),
            value: query,
          };
        }

        return {
          label: this.$t('c-electronic-instruction-tile.referenceNumber'),
          value: `${this.$t('c-electronic-instruction-tile.noMatch')}`,
        };
      },

      /**
       * Maps the status of the instruction item depending on specified flags.
       *
       * @returns {Object}
       */
      status() {
        const { item } = this;
        const statusStump = {
          code: ELECTRONIC_INSTRUCTION_STATUS_MAP.VALID,
          text: null,
          validAsOf: null,
        };

        if (item.invalidVersionDueToSafety) {
          statusStump.code = ELECTRONIC_INSTRUCTION_STATUS_MAP.INVALID;
          statusStump.text = this.$t('c-electronic-instruction-tile.statusTextInvalidDueSafety');
        } else if (item.improvedVersionExists) {
          statusStump.code = ELECTRONIC_INSTRUCTION_STATUS_MAP.IMPROVED;
          statusStump.text = this.$t('c-electronic-instruction-tile.statusTextImprovedVersionExists');
        } else if (item.validAsOf) {
          statusStump.validAsOf = this.$t('c-electronic-instruction-tile.validAsOf', {
            date: formatDate(item.validAsOf),
          });
        }

        return statusStump;
      },

      /**
       * Returns a definition of BEM modifiers.
       *
       * @returns {Object}
       */
      modifiers() {
        const { border, width, vertical, theme } = this;

        return {
          border,
          width,
          vertical,
          enableHistory: this.history,
          showHistoryToggle:
            this.history && (!!this.eifuStore.history?.[this.item.id]?.length || !this.SUPPORTS_INTERSECTION_OBSERVER),
          theme,
        };
      },

      /**
       * Returns a BEM modifiers definition for the the 'inner' element.
       *
       * @returns {Object}
       */
      innerModifiers() {
        const { status } = this;

        return {
          status: status.code.toLowerCase(),
          hasIndicator: status.code !== ELECTRONIC_INSTRUCTION_STATUS_MAP.VALID,
        };
      },

      /**
       * Checks if the history toggle should be disabled (only required for IE11).
       *
       * @returns {Boolean}
       */
      disableHistoryToggle() {
        const items = this.eifuStore.history?.[this.item.id] || this.eifuStore.relations?.[this.item.id];

        return items && !this.SUPPORTS_INTERSECTION_OBSERVER && !items.length;
      },

      /**
       * Return wheter the item is a related asset item or not.
       *
       * @returns {Boolean} - True if the item is a related asset item, false otherwise.
       */
      isRelatedAssetItem() {
        return this.relatedAssetParentItem != null;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      const search = new URLSearchParams(window.location.search);

      if (search.get(URL_PARAM_MAP.showEifuDetail) && search.get(URL_PARAM_MAP.documentId) === this.item.fileId) {
        this.onDetailClick();
      }

      if (this.history && this.SUPPORTS_INTERSECTION_OBSERVER) {
        this.intersectionObserver = new IntersectionObserver((elements) => {
          if (elements.some((element) => element.isIntersecting)) {
            this.loadHistory();
            this.intersectionObserver.disconnect();
          }
        });

        this.intersectionObserver.observe(this.$el);
      }

      window.addEventListener('resizeend', this.updatePropertyOverflowState);

      this.$mitt.on('l-electronic-instruction-list.focusDocument', this.onFocusDocument);

      setTimeout(() => {
        this.updatePropertyOverflowState();

        if (this.eifuStore.showAllDetailValues) {
          Object.values(PROPERTY_REF_MAP).forEach((element) => {
            this.onPropertyCollapseToggle(element);
          });
        }
      }); // Immediate execution causes sometimes calculation errors.
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      window.removeEventListener('resizeend', this.updatePropertyOverflowState);
      this.$mitt.off('l-electronic-instruction-list.focusDocument', this.onFocusDocument);
      this.intersectionObserver?.disconnect();
    },
    // unmounted() {},

    methods: {
      /**
       * Helper function.
       */
      getElectronicInstructionBrandNames,

      /**
       * Updates the reference, if limited height properties have currently an overflow.
       */
      updatePropertyOverflowState() {
        this.overflowingProperties = Object.values(this.PROPERTY_REF_MAP).filter((ref) => {
          const element = this.$refs[ref];

          // NOTE: due to slight differences in fontsize/lineheight depending on the theme, we allow for a small tolerance
          return !!(element && element.scrollHeight - element.clientHeight > 3);
        });
      },

      /**
       * Handles the click event on the tiles detail button.
       *
       * @param {Boolean} showAllValues - Flag to en-/disable collapsed values on detail view.
       */
      onDetailClick(showAllValues) {
        if (showAllValues === true) {
          this.eifuStore.showAllDetailValues = true;
        }

        this.eifuStore.relatedAssetFile = this.isRelatedAssetItem ? this.item : null;
        this.eifuStore.relationParentFile = this.isRelatedAssetItem ? this.relatedAssetParentItem : null;

        this.notificationStore.resetNotifications();
        this.eifuStore.detailViewFile = this.item;
      },

      /**
       * Handles the click event on the tiles download button.
       */
      onDownloadClick() {
        const { item } = this;

        if (!this.showSubscriptionOnDownload) {
          downloadFileByUrl(item.fileName, item.fileUrl);

          return;
        }

        this.notificationStore.resetNotifications();
        this.eifuStore.downloadViewFile = item;
      },

      /**
       * Handles the 'report a problem' button click.
       */
      onReportClick() {
        this.notificationStore.resetNotifications();

        /**
         * @event c-electronic-instruction-tile.reportProblem - Informs the application to report a problem about the given file.
         *
         * @type {Object} A electronic instruction file definition.
         */
        this.$mitt.emit('c-electronic-instruction-tile.reportProblem', this.item);
      },

      /**
       * Handles the 'request hard copy' button click.
       */
      onRequestHardCopyClick() {
        this.notificationStore.resetNotifications();
        this.eifuStore.requestElectronicInstructionItem = this.item;
      },

      /**
       * Handles the click event of an expand/collapse toggle button for a limited height property.
       *
       * @param {String} refIdentifier - The ref identifier for the property.
       */
      onPropertyCollapseToggle(refIdentifier) {
        this.expandedProperties[refIdentifier] = !this.expandedProperties[refIdentifier];
      },

      /**
       * Calculates BEM modifiers for properties with limited height.
       *
       * @param {String} refIdentifier - The ref name by which the property can be identified.
       *
       * @returns {Object}
       */
      getModifiersForLimitedProperty(refIdentifier) {
        return {
          limit: !this.expandedProperties[refIdentifier],
          hasOverflow: this.overflowingProperties.includes(refIdentifier),
        };
      },

      /**
       * Loads the history for the current document.
       *
       * @returns {Promise}
       */
      loadHistory() {
        const { id } = this.item;

        if (!this.isLoadingHistory && !this.eifuStore.history?.[id] && !this.eifuStore.relations?.[id]) {
          this.isLoadingHistory = true;

          return this.eifuStore.apiFetchHistory(id).finally(() => {
            this.isLoadingHistory = false;
          });
        }

        return Promise.resolve();
      },

      /**
       * Scrolls the viewport to the given document tile.
       *
       * @param {Object} document - A document definition.
       */
      scrollToDocument(document) {
        this.$nextTick(() => {
          // Wait for results sections to be rendered.
          this.$refs.historyItem
            ?.find((component) => component.item === document)
            ?.$el.scrollIntoView({ behavior: 'smooth' });
        });
      },

      /**
       * Toggles the history list.
       *
       * @param {Boolean} [expand] - Allows to set a specific state.
       */
      toggleHistory(expand = !this.isHistoryExpanded) {
        this.isHistoryExpanded = expand;
      },

      /**
       * Callback for the history toggle button.
       */
      onHistoryToggleClick() {
        this.loadHistory();
        this.toggleHistory();
      },

      /**
       * Event handler for the 'focusDocument' bus event from l-electronic-instruction-list.
       *
       * @param {Object} payload - The events payload
       * @param {Object} payload.component - The Vue component that emitted the event.
       * @param {Object} payload.document - The document definition that should be focused.
       */
      onFocusDocument(payload) {
        const { item } = this;

        if (payload.component.$el.contains(this.$el) && item === payload.document) {
          this.loadHistory().then(() => {
            const queryLastSeenOnDocument = this.eifuStore.history[item.id].find((document) => !document.isHistorical); // Find last use of searched query.

            this.toggleHistory(true);

            if (queryLastSeenOnDocument) {
              this.scrollToDocument(queryLastSeenOnDocument);
            }
          });
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';

  .c-electronic-instruction-tile {
    $this: &;
    $grid-area--icon: icon;
    $grid-area--information: information;
    $grid-area--actions: actions;
    $indicator-size: 40px;

    @extend %scroll-margin-top;

    // Sagemax start
    &--theme-sagemax {
      &#{$this}--width-responsive > #{$this}__inner {
        @include mixins.media(md) {
          grid-template-columns: 0.5fr 1fr 1.2fr;
        }

        @include mixins.media(lg) {
          grid-template-columns: 0.5fr 1fr 1.15fr;
        }
      }

      &#{$this}--vertical > #{$this}__inner {
        #{$this}__actions {
          padding: 0 functions.rem(variables.$spacing--15);
        }
      }

      #{$this}__title {
        font-weight: variables.$font-weight--semi-bold;
      }

      #{$this}__more-toggle {
        color: functions.get-theme-var('color-primary-3');
        font-weight: variables.$font-weight--regular;

        &:hover,
        &:focus {
          color: functions.get-theme-var('color-primary-3');
          text-decoration: underline;
        }
      }

      #{$this}__status-text {
        color: functions.get-theme-var('color-primary-1');
        font-weight: variables.$font-weight--semi-bold;
      }

      #{$this}__available-languages {
        font-weight: variables.$font-weight--regular;
        text-decoration-thickness: 2px;
      }

      #{$this}__property-toggle {
        text-decoration: none;

        &:hover,
        &:focus {
          text-decoration: underline;
        }
      }
    }
    // Sagemax end

    &__inner {
      position: relative;
      padding: functions.rem(variables.$spacing--25 variables.$spacing--20 variables.$spacing--20);
    }

    &__icon {
      display: flex;
      grid-area: $grid-area--icon;
      justify-content: center;
      align-items: center;
      margin-bottom: functions.rem(variables.$spacing--25);
      color: functions.get-theme-var('color-primary-1');

      .e-icon-sprite {
        width: functions.rem(60px);
        height: functions.rem(60px);
      }
    }

    &__information {
      grid-area: $grid-area--information;
      margin-bottom: functions.rem(variables.$spacing--30);
    }

    &__actions {
      grid-area: $grid-area--actions;
    }

    &__status-indicator {
      position: absolute;
      top: -2px;
      left: -2px;
      display: flex;
      justify-content: center;
      align-items: center;
      width: functions.rem($indicator-size);
      height: functions.rem($indicator-size);
      color: functions.get-theme-var('color-grayscale-1000');

      .e-icon-sprite {
        width: functions.rem(20px);
        height: functions.rem(20px);
      }
    }

    &__title {
      @include mixins.font(variables.$font-size--18);
      @include mixins.line-clamp(1, 1.2);

      margin-bottom: functions.rem(variables.$spacing--15);
    }

    &__properties {
      @include mixins.font(variables.$font-size--14, variables.$line-height--22);

      margin-bottom: 0;
    }

    &__property {
      &--limit {
        @include mixins.line-clamp(
          3,
          functions.strip-unit(math.div(variables.$line-height--22, variables.$font-size--14))
        );
      }

      &--details + &--details {
        margin-top: functions.rem(variables.$spacing--10);
      }
    }

    &__property-name {
      display: inline;
      font-weight: variables.$font-weight--regular;
    }

    &__property-value {
      display: inline;
      margin-bottom: functions.rem(variables.$spacing--5);
      font-weight: variables.$font-weight--semi-bold;

      &::before {
        content: '\00a0';
      }

      &--file-type {
        text-transform: uppercase;
      }
    }

    &__property-toggle-wrapper {
      margin-bottom: functions.rem(variables.$spacing--10);

      #{$this}__property:not(#{$this}__property--has-overflow) + & {
        display: none;
      }
    }

    &__property-toggle {
      @extend %button-reset;

      color: functions.get-theme-var('color-primary-1');
      font-weight: variables.$font-weight--semi-bold;
      text-align: left;
      text-decoration: underline;
    }

    &__status-text {
      @include mixins.font(variables.$font-size--14, variables.$line-height--22);

      margin-top: functions.rem(variables.$spacing--30);
      color: functions.get-theme-var('color-secondary-5');
    }

    &__single-language {
      @include mixins.font(variables.$font-size--14, variables.$line-height--22);

      font-weight: variables.$font-weight--semi-bold;
    }

    &__available-languages {
      @include mixins.font(variables.$font-size--14, variables.$line-height--22, variables.$font-weight--semi-bold);

      width: max-content;
      color: functions.get-theme-var('color-primary-1');
      text-decoration: underline;
    }

    &__cta:not(:last-child) {
      margin-bottom: functions.rem(variables.$spacing--15);
    }

    &__request-disclaimer {
      margin-top: functions.rem(variables.$spacing--50);
    }

    &__request-cta {
      @extend %button-reset;

      color: functions.get-theme-var('color-primary-1');
      text-align: left;
    }

    &__more-toggle {
      @extend %button-reset;

      display: inline-block;
      margin-left: 0.25em;
      color: functions.get-theme-var('color-primary-1');
      font-weight: variables.$font-weight--semi-bold;

      &:hover,
      &:focus {
        color: functions.get-theme-var('color-secondary-1');
      }
    }

    &__history {
      @extend %list-reset;

      margin-top: functions.rem(variables.$spacing--15);
    }

    &__history-item {
      margin-bottom: functions.rem(variables.$spacing--15);
    }

    &__history-toggle-wrapper {
      display: none;
      justify-content: center;
      margin-top: functions.rem(variables.$spacing--30);
      opacity: 0;
      transition: opacity variables.$transition-duration--200;
      pointer-events: none;

      @include mixins.media(md) {
        margin-top: functions.rem(variables.$spacing--55);
      }
    }

    &__archive {
      @extend %list-reset;

      margin-top: functions.rem(variables.$grid-padding--xs);

      @include mixins.media(sm) {
        margin-top: functions.rem(variables.$grid-gutter--md);
      }

      @include mixins.media(md) {
        margin-top: functions.rem(variables.$grid-gutter--lg);
      }
    }

    &--enable-history {
      #{$this}__history-toggle-wrapper {
        display: flex;
      }
    }

    &--show-history-toggle {
      #{$this}__history-toggle-wrapper {
        opacity: 1;
        pointer-events: all;
      }
    }

    &__inner--status-improved {
      #{$this}__status-indicator {
        background-color: functions.get-theme-var('color-status-warning');
      }
    }

    &__inner--status-invalid {
      #{$this}__status-text {
        color: functions.get-theme-var('color-status-error');
      }

      #{$this}__cta {
        border-color: functions.get-theme-var('color-status-error');
        color: functions.get-theme-var('color-status-error');
      }

      #{$this}__status-indicator {
        background-color: functions.get-theme-var('color-status-error');
      }
    }

    &__inner--has-indicator:not(&--width-responsive) {
      padding-top: functions.rem($indicator-size + variables.$spacing--25);
    }

    &--border-default {
      #{$this}__inner {
        border: 2px solid functions.get-theme-var('color-grayscale-900');
      }
    }

    &--border-gradient {
      #{$this}__inner {
        @extend %border-gradient;

        border-width: 1px;
      }

      #{$this}__status-indicator {
        top: -1px;
        left: -1px;
      }
    }

    &--border-none {
      #{$this}__inner {
        border: 0;
      }

      #{$this}__status-indicator {
        top: 0;
        left: 0;
      }
    }

    &--vertical {
      flex: 0 0 auto;
      width: functions.rem(370px);

      &:not(:last-child) {
        margin-right: functions.rem(variables.$grid-padding--xs);

        @include mixins.media(sm) {
          margin-right: functions.rem(variables.$grid-gutter--md);
        }

        @include mixins.media(md) {
          margin-right: functions.rem(variables.$grid-gutter--lg);
        }
      }
    }

    &--vertical > #{$this}__inner {
      display: grid;
      grid-template-areas:
        '#{$grid-area--icon}'
        '#{$grid-area--information}'
        '#{$grid-area--actions}';
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr auto;
      height: 100%;
      padding: functions.rem(variables.$spacing--40);

      #{$this}__actions {
        display: block;
      }

      #{$this}__status-text {
        margin-top: 0;
      }
    }

    &--width-responsive > #{$this}__inner {
      @include mixins.media(sm) {
        display: grid;
        grid-column-gap: functions.rem(variables.$spacing--45);
        grid-template-areas:
          '#{$grid-area--icon} #{$grid-area--information}'
          '#{$grid-area--actions} #{$grid-area--actions}';
        grid-template-columns: functions.rem(60px) 1fr;
        grid-template-rows: 1fr;
        padding: functions.rem(variables.$spacing--30 variables.$spacing--40);
      }

      @include mixins.media(md) {
        grid-template-areas: '#{$grid-area--icon} #{$grid-area--information} #{$grid-area--actions}';
        grid-template-columns: 0.5fr 1fr 1fr;
        padding: functions.rem(variables.$spacing--25 variables.$spacing--50 variables.$spacing--25 0);
      }

      #{$this}__icon {
        @include mixins.media(md) {
          justify-content: center;
          align-items: center;
          margin-bottom: 0;
        }
      }

      #{$this}__information {
        @include mixins.media(md) {
          margin-bottom: 0;
        }
      }

      #{$this}__property-value {
        @include mixins.media(md) {
          margin-bottom: 0;
        }
      }

      #{$this}__actions {
        @include mixins.media(sm) {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        @include mixins.media(md) {
          justify-content: flex-end;
        }
      }

      #{$this}__title {
        @include mixins.media(md) {
          margin-bottom: functions.rem(variables.$spacing--10);
        }
      }

      #{$this}__status-text {
        @include mixins.media(sm) {
          margin-top: functions.rem(variables.$spacing--15);
        }

        @include mixins.media(md) {
          margin-top: 0;
        }
      }

      #{$this}__cta:not(:last-child) {
        @include mixins.media(sm) {
          margin-right: functions.rem(variables.$spacing--25);
          margin-bottom: 0;
        }
      }
    }
  }
</style>
